import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Form, { Field as BaseField } from "rc-field-form";
import { Card, CardHeader, Row, Col } from "reactstrap";
import Button from "../../../../../components/Button";
import Topbar from "../../components/Topbar";
import ColumnsTable from "../../components/ColumnsTable";
import FiltersTable from "../../components/FiltersTable";
import "../style.scss";
import { useConfigurationModule } from "../../hooks/useConfigurationModule";
import { useMergeConfigurations } from "../../helpers";
import { useConfigurationActions } from "../../api/useConfiguration";

import { useFiltersDefaultValue } from "../hooks/useFiltersDefaultValue";
import { useColumnsDefaultValue } from "../hooks/useColumnsDefaultValue";

import { useColumns, useHiddenColumns } from "./useColumns";
import { useFilters } from "../../../../TimeSheet/Home/Actions/useFilters";
import OptionsTable from "../../components/OptionsTable";
import Switch from "../../../../../components/Inputs/Switch";
import Field from "../../../../../components/Field";

const options = ["columns", "filters", "settings"];

function MyTimesheet() {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const config = useConfigurationModule("myTimesheet");

    const [menu, setMenu] = useState("columns");

    const columns = useColumns();
    const filters = useFilters();

    const content = useMemo(() => {
        switch (menu) {
            default:
            case "columns": {
                return (
                    <ColumnsTable config={columns} />
                );
            }
            case "filters": {
                return (
                    <FiltersTable config={filters} form={form} />
                );
            }
            case "settings": {
                const fields = [
                    { label: t("daily-summary"), name: "hideDailySummary" },
                    { label: t("weekly-summary"), name: "hideWeeklySummary" },
                    { label: t("clock-date-picker"), name: ["timeSheet", "hideClockDatePicker"] },
                ];
                return (
                    <Row>
                        <Col xs={12} md={6}>
                            <OptionsTable fields={fields} />
                        </Col>

                        <Col xs={12} md={6}>
                            <Field name="mandatoryNoteOnEdit" valuePropName="checked">
                                <Switch>{t("mandatory-note-on-edit")}</Switch>
                            </Field>
                            <Field name="mandatoryNoteOnCreate" valuePropName="checked">
                                <Switch>{t("mandatory-note-on-create")}</Switch>
                            </Field>
                        </Col>
                    </Row>
                );
            };
        }
    }, [t, menu, columns, filters, form]);

    const { submit, submitting } = useConfigurationActions("myTimesheet");
    const onFinish = useCallback(async (formValues) => {
        console.log({ formValues })
        try {
            await submit({
                entity: "company",
                config: formValues,
            });
        } catch (err) { }
    }, [submit]);

    const defaultColumnsValue = useMergeConfigurations(
        useColumnsDefaultValue({
            columns,
            hiddenColumns: useHiddenColumns(),
        }),
        config?.columns,
    );
    const defaultFiltersValue = useMergeConfigurations(
        useFiltersDefaultValue(filters),
        config?.filters,
    );
    useEffect(() => {
        form.setFieldsValue({
            columns: defaultColumnsValue,
            filters: defaultFiltersValue,
            hideDailySummary: config?.hideDailySummary || false,
            hideWeeklySummary: config?.hideWeeklySummary || false,
            hideClockDatePicker: config?.timeSheet?.hideClockDatePicker || false,
            defaultFilter: config?.defaultFilter,
            mandatoryNoteOnEdit: config?.mandatoryNoteOnEdit || false,
            mandatoryNoteOnCreate: config?.mandatoryNoteOnCreate || false,
        })
    }, [form, config, defaultColumnsValue, defaultFiltersValue]);

    return (
        <div className="d-flex h-100 configuration">
            <Card className="flex-fill mb-0 h-100">
                <Form form={form} onFinish={onFinish} style={{ height: "calc(100% - 160px)" }}>
                    <CardHeader>
                        <Row>
                            <Col xs="6" className="pl-4 d-flex">
                                <h3 className="mb-0 mr-5">{t("my-timesheet")}</h3>
                            </Col>

                            <Col className="text-right" xs="6">
                                <Button
                                    className="btn-dark px-3 btn-round btn-icon"
                                    size="sm"
                                    loading={submitting}
                                    type="submit"
                                >
                                    {t("save")}
                                </Button>
                            </Col>
                        </Row>
                    </CardHeader>

                    <Topbar options={options} menu={menu} setMenu={setMenu} />

                    <div
                        className="px-4"
                        style={{ overflow: "auto" }}
                    >
                        <BaseField name="columns" />
                        <BaseField name="filters" />
                        <BaseField name="hideDailySummary" />
                        <BaseField name="hideWeeklySummary" />
                        <BaseField name={["timeSheet", "hideClockDatePicker"]} />
                        <BaseField name="defaultFilter" />
                        <BaseField name="mandatoryNoteOnEdit" />
                        <BaseField name="mandatoryNoteOnCreate" />

                        {content}
                    </div>
                </Form>
            </Card>
        </div>
    );
}

export default MyTimesheet;
